import React from 'react';
import { useEffect } from 'react';
import classNames from 'classnames';

import type { Pageview } from '../services/Pageview';
import Tracking from '../services/Tracking';

type Props = {
  isOpen?: boolean;
  closeModal?: () => void;
  size?: string;
  scrollable?: boolean;
  className?: string;
  children: React.ReactNode;
  as?: React.ElementType;
  hideCloseButton?: boolean;
  hideScrollOverlay?: boolean;
  header?: React.ReactNode;
  pageview?: Pageview;
  pageviewProps?: UnsafeAny[];
  onOpen?(): void;
};

const bodyClassName = 'body-modal--open';

function ModalOverlay({ onClick }) {
  useEffect(() => {
    if (!document.body.className.includes(bodyClassName)) {
      document.body.className += document.body.className.length ? ` ${bodyClassName}` : bodyClassName;
    }

    return () => {
      document.body.className = document.body.className.replace(bodyClassName, '');
    };
  }, []);

  return <div className="modal-overlay" onClick={onClick} />;
}

function Modal({
  isOpen = true,
  closeModal,
  children,
  header,
  as,
  className,
  size,
  scrollable,
  hideCloseButton = false,
  hideScrollOverlay = false,
  ...props
}: Props) {
  const handleCloseModal = () => {
    if (closeModal) {
      closeModal();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    props.onOpen?.();
    if (!props.pageview) return;
    Tracking.modalView(props.pageview, ...(props.pageviewProps ?? []));
  }, [isOpen, props.pageviewProps]);

  if (!isOpen) return null;

  const Component = as ?? 'div';
  const CloseButton = () => (
    <button aria-label="Close" className="close" data-dismiss="modal" onClick={handleCloseModal} type="button">
      <i aria-hidden="true" className="icon icon-close" />
    </button>
  );

  return (
    <div aria-hidden="true" className={classNames('modal', className)} tabIndex={-1}>
      <div
        className={classNames('modal-dialog', size ? `modal-${size}` : null, {
          'modal-dialog-scrollable': scrollable,
          'modal-dialog-scrollable-overlay': scrollable && !hideScrollOverlay,
        })}
        role="document"
      >
        <Component className="modal-content">
          {header ? (
            <div className="modal-header justify-content-between no-border pb-2">
              <>
                {header}
                {!hideCloseButton && <CloseButton />}
              </>
            </div>
          ) : (
            !hideCloseButton && (
              <div className="modal-header justify-content-end no-border pb-2">
                <CloseButton />
              </div>
            )
          )}
          {children}
        </Component>
      </div>
      <ModalOverlay onClick={handleCloseModal} />
    </div>
  );
}

export default Modal;
